import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Button,
  Tabs,
  Tab,
  Divider,
  Alert,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableRow,
  TableCell,
} from "@mui/material";
import { ArrowBack, UploadFile, Receipt, Edit, CallMerge, Delete, Inventory2Outlined } from "@mui/icons-material";
import dayjs from "dayjs";

import useShipments from "../../hooks/useShipments";
import StatusChip from "../../components/common/StatusChip";
import ShipmentTracker from "../../components/common/ShipmentTracker";
import ShipmentTimeline from "../../components/shipments/ShipmentTimeline";
import SkeletonLoader from "../../components/common/SkeletonLoader";
import EmptyState from "../../components/common/EmptyState";
import { selectKeyForRole, selectStatusOrder } from "../../redux/slices/statusDefinitionsSlice";
import { selectCurrentUser, selectActiveTin, setActiveTin } from "../../redux/slices/authSlice";
import ConsolidationPickerDialog from "../../components/common/ConsolidationPickerDialog";
import PromoRibbon from "../../components/common/PromoRibbon";
import { formatUsd } from "../../utils/currency";
import { useSnackbar } from "notistack";
import useUrlState from "../../hooks/useUrlState";
import { slugifyTab } from "../../utils/tabSlug";

const DETAIL_TABS = ["Details", "Items", "Status History", "Carrier Tracking", "Documents"];

// Module-level (not inline in the component) so useUrlState gets a stable
// reference every render — see that hook's own doc comment for why.
const URL_SCHEMA = {
  tab: { default: "" },
};

const CustomerShipmentDetail = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const user = useSelector(selectCurrentUser);
  const activeTin = useSelector(selectActiveTin);
  const scheduledForDeliveryKey = useSelector(selectKeyForRole("shipment", "triggers_delivery_pool"));
  const itemReceivedKey = useSelector(selectKeyForRole("item", "received"));
  const itemStatusOrder = useSelector(selectStatusOrder("item"));
  const itemPendingKey = itemStatusOrder[0];
  const receivedIdx = itemStatusOrder.indexOf(itemReceivedKey);
  // Eligible for a consolidation only while still pending or received —
  // once it moves to scheduled_for_sailing (or anything after), it's
  // already committed to its own timeline.
  const isItemConsolidationEligible = (item) => {
    if (item.consolidation) return false;
    const idx = item.itemStatus ? itemStatusOrder.indexOf(item.itemStatus) : 0;
    return receivedIdx === -1 || idx <= receivedIdx;
  };
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { current: shipment, loading, error, loadOne, update, remove } = useShipments();

  const [urlState, setUrlState] = useUrlState(URL_SCHEMA);
  const tab = Math.max(0, DETAIL_TABS.findIndex((l) => slugifyTab(l) === urlState.tab));
  // Consolidation picker — opened for either the whole shipment
  // ("Request Consolidation") or a single item ("Add to Consolidation"),
  // never auto-merged silently; the dialog lets the customer explicitly
  // pick one of their own existing open consolidations, or start a new one.
  const [consolidationPicker, setConsolidationPicker] = useState(null); // { mode, itemId? }

  useEffect(() => {
    if (id) loadOne(id);
  }, [id]);

  const handleDelete = async () => {
    if (!window.confirm(`Delete shipment ${shipment.trackingNumber}? This cannot be undone.`)) return;
    const ok = await remove(id);
    if (ok) navigate("/dashboard/shipments");
  };

  if (loading && !shipment) {
    return <SkeletonLoader rows={6} />;
  }

  // Reached either by a shipment that genuinely doesn't exist, or one that
  // belongs to someone else — the server returns 403/404 either way (see
  // shipmentController.getShipment), and this is the only place in the app
  // that fetches a shipment directly by ID/tracking number from the URL
  // rather than from an already-scoped list, so it's the one spot that
  // actually needs to handle "not mine" explicitly instead of just
  // rendering nothing.
  if (!shipment) {
    return (
      <Box
        sx={{
          minHeight: "calc(100vh - 220px)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <EmptyState
          icon="🚫"
          title="Shipment not found"
          description={
            error === "Access denied"
              ? "This shipment doesn't belong to your account."
              : "This shipment doesn't exist, or the link may be incorrect."
          }
          actionLabel="Back to Shipments"
          onAction={() => navigate("/dashboard/shipments")}
        />
      </Box>
    );
  }

  // A multi-TIN customer only ever operates under one selected TIN — a
  // shipment filed under a different one (e.g. reached via a notification,
  // the dashboard's recent list before it re-scoped, or a typed/bookmarked
  // URL) shouldn't be viewable or editable until they switch to it. This is
  // the one place that can't rely on the list already being filtered, since
  // it's reached directly by trackingNumber.
  const belongsToOtherTin =
    !!user?.canManageMultipleTins && !!shipment.tin && !!activeTin && shipment.tin !== activeTin;

  if (belongsToOtherTin) {
    const tinEntry = (user.tins || []).find((t) => t.tin === shipment.tin);
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "flex-start" }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => (window.history.state?.idx > 0 ? navigate(-1) : navigate("/dashboard/shipments"))}
          variant="outlined"
          size="small"
        >
          Back
        </Button>
        <Alert severity="info" sx={{ borderRadius: 2, width: "100%" }}>
          This shipment is filed under {tinEntry?.label ? `${shipment.tin} (${tinEntry.label})` : shipment.tin}, not
          your currently selected TIN. Switch to it to view or edit this shipment.
        </Alert>
        <Button
          variant="contained"
          onClick={() => dispatch(setActiveTin(shipment.tin))}
        >
          Switch to {tinEntry?.label ? `${shipment.tin} (${tinEntry.label})` : shipment.tin}
        </Button>
      </Box>
    );
  }

  const needsInvoice = shipment.status === "awaiting_invoice";
  // Server-computed from each item's own status (see
  // shipmentStatusService.computeCustomerEditable) — editable until EVERY
  // item has reached "Scheduled for Sailing" or later, not just while the
  // shipment's collapsed aggregate status happens to be one of a fixed
  // list (that used to block editing as soon as ANY one item shipped).
  const isEditable = shipment.customerEditable !== false;

  const receivedCount =
    shipment.items?.filter((i) => i.itemStatus && i.itemStatus !== itemPendingKey).length || 0;
  const totalItems = shipment.items?.length || 0;
  const hasPromoRibbon = shipment.promoStatus === "applied" || shipment.promoStatus === "pending";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      {/* Header */}
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}
      >
        <Button
          startIcon={<ArrowBack />}
          onClick={() => (window.history.state?.idx > 0 ? navigate(-1) : navigate("/dashboard/shipments"))}
          variant="outlined"
          size="small"
        >
          Back
        </Button>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexWrap: "wrap",
            }}
          >
            <Typography variant="h4" fontWeight={700}>
              {shipment.packageDescription || shipment.vendor?.name}
            </Typography>
            <StatusChip status={shipment.status} domain="shipment" />
          </Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontFamily: "monospace" }}
          >
            {shipment.trackingNumber} · {shipment.vendor?.name} · Created{" "}
            {dayjs(shipment.createdAt).format("MMMM D, YYYY")}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {isEditable && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<Edit />}
              onClick={() => navigate(`/dashboard/shipment/${id}/edit`)}
            >
              Edit
            </Button>
          )}
          {isEditable && (
            <Button
              variant="outlined"
              size="small"
              color="error"
              startIcon={<Delete />}
              onClick={handleDelete}
            >
              Delete
            </Button>
          )}
          {shipment.items?.some(isItemConsolidationEligible) && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<CallMerge />}
              onClick={() => setConsolidationPicker({ mode: "shipment" })}
            >
              Consolidate
            </Button>
          )}
          <Button
            variant="contained"
            size="small"
            startIcon={<UploadFile />}
            onClick={() => navigate(`/dashboard/upload/${id}`)}
          >
            Upload Docs
          </Button>
        </Box>
      </Box>

      {/* Action alert */}
      {needsInvoice && (
        <Alert
          severity="warning"
          action={
            <Button
              color="warning"
              size="small"
              onClick={() => navigate(`/dashboard/upload/${id}`)}
            >
              Upload Invoice
            </Button>
          }
        >
          <strong>Action required —</strong> Upload your purchase invoice to
          proceed with customs declaration.
        </Alert>
      )}

      {shipment.status === scheduledForDeliveryKey && (
        <Alert severity="success" icon="🎉">
          <strong>Your package is ready for pickup!</strong> Visit George Town
          Warehouse with valid ID.
        </Alert>
      )}

      {/* Tracker */}
      <Card sx={{ position: "relative", overflow: "hidden" }}>
        <PromoRibbon promoStatus={shipment.promoStatus} />
        <CardContent
          sx={{
            pt: 3,
            pb: 3,
            px: hasPromoRibbon ? 5 : 3,
          }}
        >
          <ShipmentTracker status={shipment.status} />
          {totalItems > 0 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 1.5 }}>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.75,
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 5,
                  bgcolor: receivedCount === totalItems ? "rgba(0,166,178,0.12)" : "rgba(0,0,0,0.05)",
                }}
              >
                <Inventory2Outlined
                  sx={{ fontSize: 16, color: receivedCount === totalItems ? "#00a6b2" : "text.secondary" }}
                />
                <Typography
                  variant="caption"
                  fontWeight={600}
                  sx={{ color: receivedCount === totalItems ? "#00a6b2" : "text.secondary" }}
                >
                  {receivedCount} of {totalItems} item{totalItems !== 1 ? "s" : ""} received
                </Typography>
              </Box>
            </Box>
          )}
          {shipment.estimatedArrivalDate && (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              textAlign="center"
              mt={2}
            >
              Estimated arrival in Cayman:{" "}
              <strong>
                {dayjs(shipment.estimatedArrivalDate).format("MMMM D, YYYY")}
              </strong>
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Tabs */}
      <Card>
        <Tabs
          value={tab}
          onChange={(_, v) => setUrlState({ tab: slugifyTab(DETAIL_TABS[v]) })}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            px: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
            "& .MuiTab-root": {
              fontSize: 13,
              textTransform: "none",
              fontWeight: 600,
              minHeight: 44,
            },
          }}
        >
          <Tab label="Details" />
          <Tab label="Items" />
          <Tab label="Status History" />
          <Tab label="Carrier Tracking" />
          <Tab label="Documents" />
        </Tabs>

        {/* Details */}
        {tab === 0 && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 0,
            }}
          >
            <Box
              sx={{
                p: 2.5,
                borderRight: { md: "1px solid" },
                borderBottom: { xs: "1px solid", md: "none" },
                borderColor: "divider",
              }}
            >
              <Typography
                variant="overline"
                color="text.secondary"
                display="block"
                mb={1.5}
              >
                Vendor / Store
              </Typography>
              {[
                { label: "Store Name", value: shipment.vendor?.name },
                { label: "Country", value: shipment.vendor?.country },
                { label: "Website", value: shipment.vendor?.website || "—" },
                { label: "Order Number", value: shipment.orderNumber || "—" },
                { label: "Carrier", value: shipment.carrier || "—" },
                {
                  label: "Carrier Tracking",
                  value: shipment.trackingNumber || "—",
                },
              ].map((r) => (
                <Box
                  key={r.label}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    py: 0.875,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    "&:last-child": { borderBottom: "none" },
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {r.label}
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {r.value}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Box sx={{ p: 2.5 }}>
              <Typography
                variant="overline"
                color="text.secondary"
                display="block"
                mb={1.5}
              >
                Package & Value
              </Typography>
              {[
                {
                  label: "Description",
                  value: shipment.packageDescription || "—",
                },
                {
                  label: "Invoice Total",
                  value:
                    shipment.invoiceCurrency && shipment.invoiceCurrency !== "USD"
                      ? `${formatUsd(shipment.invoiceTotal)} (${shipment.invoiceTotalOriginal} ${shipment.invoiceCurrency})`
                      : formatUsd(shipment.invoiceTotal),
                },
                {
                  label: "Weight",
                  value: shipment.totalWeight ? `${shipment.totalWeight} lbs` : "Not yet measured",
                },
                {
                  label: "CBF",
                  // displayTotalCbf applies the admin-configured minimum-CBF
                  // floor — falls back to the real totalCbf if that field
                  // isn't present for some reason, so this never breaks.
                  value:
                    shipment.displayTotalCbf ?? shipment.totalCbf
                      ? `${shipment.displayTotalCbf ?? shipment.totalCbf} ft³`
                      : "Not yet measured",
                },
                {
                  label: "Customs Status",
                  value: shipment.customsDeclarationSubmitted
                    ? "Submitted"
                    : "Pending",
                },
                {
                  label: "Sailing Schedule",
                  value:
                    shipment.sailingSchedule?.sailingNumber || "Not assigned",
                },
                {
                  label: "ETA Cayman",
                  value: shipment.estimatedArrivalDate
                    ? dayjs(shipment.estimatedArrivalDate).format("MMM D, YYYY")
                    : "N/A",
                },
              ].map((r) => (
                <Box
                  key={r.label}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    py: 0.875,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    "&:last-child": { borderBottom: "none" },
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {r.label}
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {r.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Items */}
        {tab === 1 && (
          <Box sx={{ p: 2.5 }}>
            {shipment.items?.length > 0 ? (
              <Box sx={{ overflowX: "auto" }}>
                <Table size="small" sx={{ minWidth: 560 }}>
                  <TableBody>
                    <TableRow
                      sx={{
                        "& td": {
                          fontWeight: 600,
                          color: "text.secondary",
                          fontSize: 11,
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                        },
                      }}
                    >
                      <TableCell>Description</TableCell>
                      <TableCell align="center">Qty</TableCell>
                      <TableCell align="center">Status</TableCell>
                    </TableRow>
                    {shipment.items.map((item, i) => (
                      <TableRow key={i}>
                        <TableCell>{item.description}</TableCell>
                        <TableCell align="center">{item.quantity}</TableCell>
                        <TableCell align="center">
                          <StatusChip status={item.itemStatus} domain="item" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            ) : (
              <Typography color="text.secondary" variant="body2">
                No items added yet.
              </Typography>
            )}
          </Box>
        )}

        {/* Status History */}
        {tab === 2 && (
          <ShipmentTimeline statusHistory={shipment.statusHistory} />
        )}

        {/* Carrier Tracking */}
        {tab === 3 && (
          <Box sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h6" mb={2}>
              Carrier Tracking (Item Level)
            </Typography>

            {shipment.items?.length > 0 ? (
              shipment.items.map((item, index) => (
                <Card
                  key={index}
                  sx={{
                    mb: 2,
                    p: 2,
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  {/* Item header */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                      flexWrap: "wrap",
                      gap: 1,
                    }}
                  >
                    <Typography fontWeight={700}>{item.description}</Typography>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      <StatusChip status={item.itemStatus} domain="item" />
                      <Chip
                        label={
                          item.carrier || shipment.carrier || "Carrier N/A"
                        }
                        size="small"
                        variant="outlined"
                      />
                      <Chip
                        label={item.trackingNumber || "No Tracking"}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                      {item.consolidation ? (
                        <Chip
                          icon={<CallMerge sx={{ fontSize: 14 }} />}
                          label="In Consolidation"
                          size="small"
                          variant="outlined"
                        />
                      ) : (
                        isItemConsolidationEligible(item) && (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<CallMerge sx={{ fontSize: 14 }} />}
                            onClick={() => setConsolidationPicker({ mode: "item", itemId: item._id })}
                          >
                            Add to Consolidation
                          </Button>
                        )
                      )}
                    </Box>
                  </Box>

                  <Box sx={{ px: { xs: 0, sm: 1 }, mb: 2 }}>
                    <ShipmentTracker status={item.itemStatus} domain="item" />
                  </Box>

                  {/* Human-readable status */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 0.5,
                      flexWrap: "wrap",
                    }}
                  >
                    <Typography variant="body2">
                      Carrier Status:
                      <strong>
                        {item.tracking?.trackingStatus === "Pending" ? "No Status Update" : item.tracking?.trackingStatus}
                      </strong>
                    </Typography>
                    {item.tracking?.trackingStatusCode && (
                      <Chip
                        label={item.tracking.trackingStatusCode}
                        size="small"
                        color={
                          item.tracking.trackingStatusCode === "delivered"
                            ? "success"
                            : item.tracking.trackingStatusCode === "transit"
                              ? "info"
                              : item.tracking.trackingStatusCode === "exception"
                                ? "error"
                                : "default"
                        }
                        variant="outlined"
                        sx={{ fontSize: 10, height: 20, textTransform: "capitalize" }}
                      />
                    )}
                  </Box>

                  {item.tracking?.lastTrackingUpdate && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      mb={1}
                    >
                      Last updated:{" "}
                      {new Date(
                        item.tracking.lastTrackingUpdate,
                      ).toLocaleString()}
                    </Typography>
                  )}

                  <Divider sx={{ my: 1.5 }} />

                  {item.tracking?.trackingEvents?.length > 0 ? (
                    item.tracking.trackingEvents.map((event, i) => (
                      <Box key={i} sx={{ mb: 1.5 }}>
                        <Typography fontWeight={600} fontSize={13}>
                          {event.description || "Update"}
                        </Typography>

                        <Typography variant="caption" color="text.secondary">
                          {event.timestamp
                            ? new Date(event.timestamp).toLocaleString()
                            : "—"}
                        </Typography>

                        {event.location && (
                          <Typography variant="caption" display="block">
                            📍 {event.location}
                          </Typography>
                        )}
                      </Box>
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No tracking updates yet.
                    </Typography>
                  )}
                </Card>
              ))
            ) : (
              <Typography color="text.secondary">
                No items found for tracking.
              </Typography>
            )}
          </Box>
        )}

        {/* Documents */}
        {tab === 4 && (
          <Box sx={{ p: 2.5, textAlign: "center" }}>
            <Typography color="text.secondary" variant="body2" mb={2}>
              {shipment.documents?.length > 0
                ? `${shipment.documents.length} document(s) uploaded`
                : "No documents uploaded yet."}
            </Typography>
            <Button
              variant="outlined"
              startIcon={<UploadFile />}
              onClick={() => navigate(`/dashboard/upload/${id}`)}
            >
              Upload Documents
            </Button>
          </Box>
        )}
      </Card>


      <ConsolidationPickerDialog
        open={!!consolidationPicker}
        onClose={() => setConsolidationPicker(null)}
        mode={consolidationPicker?.mode}
        shipmentId={id}
        customerId={shipment.customer?._id || shipment.customer}
        itemId={consolidationPicker?.itemId}
        onDone={() => loadOne(id)}
      />
    </Box>
  );
};

export default CustomerShipmentDetail;